/**
 * Deterministic per-name color picker.
 *
 * Hashes the input string and maps it to a hue on the OKLCH-like HSL wheel,
 * keeping saturation and lightness in a readable range for dark backgrounds.
 * The same name always yields the same color, across sessions and clients.
 */

/** Curated palette of readable colors for chat nicknames. */
const PALETTE = [
  '#8fb0ff', // light blue
  '#6bd996', // green
  '#e0a93a', // amber
  '#e88a5a', // orange
  '#d96fdb', // magenta
  '#5fd4d4', // teal
  '#f06a8a', // pink
  '#9fa7d8', // periwinkle
  '#7ed957', // lime
  '#ffb84d', // gold
  '#c792ea', // purple
  '#56b6c2', // cyan
];

/**
 * Hash a string to a non-negative integer (FNV-1a variant).
 * Simple, fast, and well-distributed for short strings.
 */
function hashString(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

/**
 * Return a stable, readable color for a given name.
 * Uses a curated palette so colors are always pleasant and readable.
 */
export function nameColor(name: string): string {
  if (!name) return PALETTE[0] ?? '#8fb0ff';
  return PALETTE[hashString(name) % PALETTE.length] ?? '#8fb0ff';
}
